import { toUserZoned, fromUserZoned } from '../../../shared/lib/tzDate';
import i18n from '../../../i18n';

// slice 02.1 · paso b — funciones puras de la vista de tareas (matemática de la grilla Gantt,
// rangos de fecha, expansión de recurrentes, formato de etapa/tag/fecha/tiempo). Extraídas
// verbatim de `ComponentTaskHoy.jsx` — sin dependencias del componente, solo `tzDate` + el
// singleton `i18n` + globals de `Date`/`Intl`.

export const TIME_SLOTS = [
    "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
    "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00"
];

// Stages list — orden único, canónico, usado en las 3 vistas que muestran las etapas (tablero
// kanban, dropdown de "Etapa" en la vista lista, flow chart). Antes había 3 arreglos hardcodeados
// por separado con órdenes distintos entre sí (ej. "check" antes que "synk" en el kanban, pero al
// revés en el dropdown) — visualmente inconsistente entre vistas aunque cada una funcionara bien
// por su cuenta. El orden elegido es el que ya tenía el tablero kanban.
export const STAGES = ['planeada', 'en proceso', 'check', 'retraso', 'bloqueo', 'synk', 'done'];
export const SELECTOR_STAGES = STAGES;

export const ROW_STAGE_ORDER = {
    'bloqueo': 1,
    'retraso': 2,
    'synk': 3,
    'en proceso': 4,
    'planeada': 5,
    'check': 6,
    'done': 7
};

// Función pura sin
// dependencias del componente — a nivel de módulo para no recrearse en cada render.
// Fase timezones (sevilla-timezones): "hoy" se calcula en `userTz` (el timezone configurado por
// el usuario en Perfil), no en el del navegador -- `toUserZoned` devuelve un Date cuyos getters
// LOCALES (getHours, getDate, setDate, etc.) ya reflejan el wall-clock en `userTz`, así que el
// resto de la función puede seguir usando esos mismos getters/setters sin cambios.
export const calculateDateRange = (timeRange, dateOffset, userTz) => {
    const today = toUserZoned(new Date(), userTz);
    today.setHours(0, 0, 0, 0);

    let start = new Date(today);
    let end = new Date(today);

    if (timeRange === 'hoy') {
        start.setDate(today.getDate() + dateOffset);
        end = new Date(start);
    } else if (timeRange === 'week') {
        const currentDay = today.getDay(); // 0 is Sunday
        const diff = today.getDate() - currentDay + (currentDay === 0 ? -6 : 1);
        start = new Date(today.setDate(diff));
        start.setDate(start.getDate() + (dateOffset * 7));
        end = new Date(start);
        end.setDate(end.getDate() + 6);
    } else if (timeRange === 'month') {
        start.setMonth(today.getMonth() + dateOffset, 1);
        end = new Date(start);
        end.setMonth(start.getMonth() + 1, 0);
    } else if (timeRange === '3_months') {
        start.setMonth(today.getMonth() - 2 + (dateOffset * 3), 1);
        end = new Date(start);
        end.setMonth(start.getMonth() + 3, 0);
    } else if (timeRange === '6_months') {
        start.setMonth(today.getMonth() - 5 + (dateOffset * 6), 1);
        end = new Date(start);
        end.setMonth(start.getMonth() + 6, 0);
    } else if (timeRange === '1_year') {
        start.setFullYear(today.getFullYear() + dateOffset, 0, 1);
        end = new Date(start);
        end.setFullYear(start.getFullYear(), 11, 31);
    }

    // Construye YYYY-MM-DD directo desde los getters LOCALES -- correcto tanto para un Date
    // normal como para uno "zoneado" por `toUserZoned` (donde `.toISOString()`, que siempre lee
    // en UTC, daría el día equivocado).
    const toYMD = (d) => {
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    return { startStr: toYMD(start), endStr: toYMD(end), startDate: start, endDate: end };
};

export const expandRecurrentTasks = (rawTasks, startStr, endStr, userTz) => {
    const expanded = [];

    rawTasks.forEach(t => {
        expanded.push(t);

        if (t.recurrent_interval && t.recurrent_interval > 0 && t.recurrent_unit && t.date) {
            // Determine if t.date is ISO or YYYY-MM-DD
            const baseDateStr = t.date.includes('T') ? t.date.split('T')[0] : t.date;
            const [year, month, day] = baseDateStr.split('-');
            if (year && month && day) {
                let current = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
                let occurrencesCount = 0;

                // Increase limit drastically to support old tasks (3 years of daily recurrence = ~1000)
                while (occurrencesCount < 1500) {
                    if (t.recurrent_unit === 'dias') current.setDate(current.getDate() + t.recurrent_interval);
                    else if (t.recurrent_unit === 'weeks' || t.recurrent_unit === 'semanas') current.setDate(current.getDate() + (t.recurrent_interval * 7));
                    else if (t.recurrent_unit === 'meses') current.setMonth(current.getMonth() + t.recurrent_interval);
                    else if (t.recurrent_unit === 'años') current.setFullYear(current.getFullYear() + t.recurrent_interval);
                    else break; // Prevent infinite loop if unit is invalid

                    const pad = (n) => String(n).padStart(2, '0');
                    const occStr = `${current.getFullYear()}-${pad(current.getMonth() + 1)}-${pad(current.getDate())}`;

                    if (occStr > endStr) break;

                    if (occStr >= startStr && occStr <= endStr && occStr !== baseDateStr) {
                        // Calculate new start_time and end_time combining new date with original time
                        let newStartTime = t.start_time;
                        let newEndTime = t.end_time;
                        if (t.start_time && t.end_time) {
                            // Fase timezones: la hora de la ocurrencia original se lee en
                            // `userTz` (no en el timezone del navegador), y el resultado se
                            // reinterpreta como wall-clock-en-`userTz` con `fromUserZoned` para
                            // obtener el instante UTC correcto -- mantiene la misma hora local
                            // (para el usuario) en cada ocurrencia recurrente.
                            const origStart = toUserZoned(new Date(t.start_time), userTz);
                            const origEnd = toUserZoned(new Date(t.end_time), userTz);

                            const newStartD = new Date(current.getFullYear(), current.getMonth(), current.getDate(), origStart.getHours(), origStart.getMinutes(), origStart.getSeconds());
                            const newEndD = new Date(current.getFullYear(), current.getMonth(), current.getDate(), origEnd.getHours(), origEnd.getMinutes(), origEnd.getSeconds());

                            // If duration crosses midnight, end date should be adjusted
                            if (origEnd.getHours() < origStart.getHours()) {
                                newEndD.setDate(newEndD.getDate() + 1);
                            }

                            newStartTime = fromUserZoned(newStartD, userTz).toISOString();
                            newEndTime = fromUserZoned(newEndD, userTz).toISOString();
                        }

                        expanded.push({
                            ...t,
                            id: `${t.id}_${occStr}`,
                            date: occStr,
                            original_id: t.id,
                            start_time: newStartTime,
                            end_time: newEndTime
                        });
                    }
                    occurrencesCount++;
                }
            }
        }
    });
    return expanded;
};

export const getStageClass = (stage) => {
    if (!stage) return 'planeada';
    const s = String(stage).toLowerCase();
    if (s === 'planeado' || s === 'planeacion') return 'planeada';
    if (s === 'block' || s === 'bloqueo') return 'bloqueo';
    if (s === 'en proceso') return 'en-proceso';
    return s.replace(/\s+/g, '-');
};

export const getStageColor = (stage) => {
    const s = stage ? String(stage).toLowerCase() : '';
    if (s === 'planeada' || s === 'planeado' || s === 'planeacion') return '#9ca3af'; // gris
    if (s === 'en proceso') return '#60a5fa'; // azul
    if (s === 'check') return '#eab308'; // amarillo
    if (s === 'synk') return '#a855f7'; // morado
    if (s === 'done') return '#10b981'; // verde
    if (s === 'bloqueo' || s === 'block') return '#ef4444'; // rojo
    if (s === 'retraso') return '#f97316'; // naranja
    return '#ccc';
};


// --- Dynamic Gantt Axis Helpers ---
export const addDays = (dateStr, days) => {
    if (!dateStr) return '';
    const d = new Date(`${dateStr}T12:00:00Z`);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
};

export const getDaysDiff = (startStr, endStr) => {
    if (!startStr || !endStr) return 0;
    const d1 = new Date(`${startStr}T12:00:00Z`);
    const d2 = new Date(`${endStr}T12:00:00Z`);
    return Math.round(d2 - d1) / (1000 * 60 * 60 * 24);
};

export const getGanttConfig = (timeRange, startStr, endStr) => {
    let headers = [];
    let subColWidth = 80;
    let totalSubCols = 0;

    let durationDays = getDaysDiff(startStr, endStr) + 1;
    if (isNaN(durationDays) || durationDays <= 0) durationDays = 1;

    let mode = timeRange;
    if (timeRange === 'custom') {
        if (durationDays <= 31) mode = 'month';
        else if (durationDays <= 90) mode = '3_months';
        else mode = '6_months';
    }

    if (mode === 'hoy') {
        subColWidth = 80;
        totalSubCols = 25;
        const TIME_SLOTS = [
            "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
            "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
            "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00"
        ];
        headers = TIME_SLOTS.map(l => ({ label: l, width: 80 }));
    }
    else if (mode === 'week') {
        subColWidth = 80;
        totalSubCols = durationDays * 5;
        for (let i = 0; i < durationDays; i++) {
            const d = new Date(`${startStr}T12:00:00Z`);
            d.setDate(d.getDate() + i);
            const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
            headers.push({ label: `${dayNames[d.getDay()]} ${d.getDate()}`, width: subColWidth * 5 });
        }
    }
    else if (mode === 'month') {
        subColWidth = 60;
        totalSubCols = durationDays;
        for (let i = 0; i < durationDays; i++) {
            const d = new Date(`${startStr}T12:00:00Z`);
            d.setDate(d.getDate() + i);
            headers.push({ label: `${d.getDate()}`, width: subColWidth });
        }
    }
    else if (mode === '3_months') {
        subColWidth = 30;
        totalSubCols = durationDays;
        let currentMonth = -1;
        let daysInMonth = 0;
        let monthName = '';

        for (let i = 0; i < durationDays; i++) {
            const d = new Date(`${startStr}T12:00:00Z`);
            d.setDate(d.getDate() + i);
            if (d.getMonth() !== currentMonth) {
                if (daysInMonth > 0) {
                    headers.push({ label: monthName, width: daysInMonth * subColWidth });
                }
                currentMonth = d.getMonth();
                monthName = d.toLocaleString('es-ES', { month: 'short' }).toUpperCase();
                daysInMonth = 1;
            } else {
                daysInMonth++;
            }
        }
        if (daysInMonth > 0) {
            headers.push({ label: monthName, width: daysInMonth * subColWidth });
        }
    }
    else if (mode === '6_months' || mode === '1_year') {
        subColWidth = mode === '6_months' ? 120 : 60;
        const totalWeeks = Math.ceil(durationDays / 7);
        totalSubCols = totalWeeks;
        for (let i = 0; i < totalWeeks; i++) {
            headers.push({ label: `Sem ${i + 1}`, width: subColWidth });
        }
    }

    return { headers, subColWidth, totalSubCols, mode, durationDays };
};

export const calculateTaskGanttPos = (task, config, startStr, userTz) => {
    let startPos = 0;
    let widthSize = 80;

    const taskDateStr = task.date || startStr;
    let dayOffset = getDaysDiff(startStr, taskDateStr);
    if (dayOffset < 0) dayOffset = 0;

    let startD = task.start_time ? new Date(task.start_time) : null;
    let endD = task.end_time ? new Date(task.end_time) : null;

    if (startD && endD) {
        // Fase timezones: la hora de inicio para POSICIONAR en la grilla se lee en `userTz`
        // (toUserZoned) -- `durationHours` sigue viniendo de la resta de instantes UTC reales
        // (endD - startD), invariante ante timezone, no debe zonearse.
        const zonedStart = toUserZoned(startD, userTz);
        const startHour = zonedStart.getHours() + (zonedStart.getMinutes() / 60);
        const durationHours = (endD - startD) / 3600000;

        if (config.mode === 'hoy') {
            const hoursFrom8 = Math.max(0, startHour - 8);
            startPos = (hoursFrom8 * (config.subColWidth * 2)) + 4;
            widthSize = (durationHours * (config.subColWidth * 2)) - 8;
        }
        else if (config.mode === 'week') {
            const dayStartPos = dayOffset * (config.subColWidth * 5);
            const slotsFromStart = startHour * (5 / 24);
            startPos = dayStartPos + (slotsFromStart * config.subColWidth) + 4;
            widthSize = (durationHours * (5 / 24) * config.subColWidth) - 8;
        }
        else if (config.mode === 'month' || config.mode === '3_months') {
            const dayWidth = config.subColWidth;
            const dayFraction = startHour / 24;
            startPos = dayOffset * dayWidth + (dayFraction * dayWidth) + 2;
            const widthFraction = durationHours / 24;
            widthSize = Math.max(dayWidth / 3, widthFraction * dayWidth) - 4;
        }
        else if (config.mode === '6_months' || config.mode === '1_year') {
            const weekWidth = config.subColWidth;
            const exactWeeks = dayOffset / 7;
            const weekFraction = startHour / (24 * 7);
            startPos = exactWeeks * weekWidth + (weekFraction * weekWidth) + 2;
            const widthFraction = durationHours / (24 * 7);
            widthSize = Math.max(weekWidth / 4, widthFraction * weekWidth) - 4;
        }
        return { startPos, widthSize };
    }

    // Fallback if no start_time exists (legacy or just created without event)
    const startColTime = task.start_col !== undefined ? parseFloat(task.start_col) : 0;
    const durationColsTime = task.duration_cols !== undefined ? parseFloat(task.duration_cols) : 2;

    if (config.mode === 'hoy') {
        startPos = (startColTime * config.subColWidth) + 4;
        widthSize = (durationColsTime * config.subColWidth) - 8;
    }
    else if (config.mode === 'week') {
        const dayStartPos = dayOffset * (config.subColWidth * 5);
        const slotIndex = Math.floor((startColTime / 24) * 5);
        startPos = dayStartPos + (slotIndex * config.subColWidth) + 4;
        const widthSlots = Math.max(1, Math.ceil(durationColsTime / 24) * 5);
        widthSize = (widthSlots * config.subColWidth) - 8;
    }
    else if (config.mode === 'month' || config.mode === '3_months') {
        const dayWidth = config.subColWidth;
        const dayFraction = startColTime / 24;
        startPos = dayOffset * dayWidth + (dayFraction * dayWidth) + 2;
        const widthFraction = durationColsTime / 24;
        widthSize = Math.max(dayWidth / 3, widthFraction * dayWidth) - 4;
    }
    else if (config.mode === '6_months' || config.mode === '1_year') {
        const weekWidth = config.subColWidth;
        const exactWeeks = dayOffset / 7;
        const weekFraction = startColTime / (24 * 7);
        startPos = exactWeeks * weekWidth + (weekFraction * weekWidth) + 2;
        const widthFraction = durationColsTime / (24 * 7);
        widthSize = Math.max(weekWidth / 4, widthFraction * weekWidth) - 4;
    }

    return { startPos, widthSize };
};

export const calculateDropToTaskUpdate = (dropX, config, startStr, durationHours = 1, userTz) => {
    let newDateStr = startStr;
    let hoursFromStart = 0;

    if (config.mode === 'hoy') {
        hoursFromStart = dropX / (config.subColWidth * 2);
    }
    else if (config.mode === 'week') {
        const dropSubCol = Math.floor(dropX / config.subColWidth);
        const dayOffset = Math.floor(dropSubCol / 5);
        newDateStr = addDays(startStr, dayOffset);
        const slotInDay = dropSubCol % 5;
        hoursFromStart = slotInDay * (24 / 5);
    }
    else if (config.mode === 'month' || config.mode === '3_months') {
        const exactDays = dropX / config.subColWidth;
        const dayOffset = Math.floor(exactDays);
        newDateStr = addDays(startStr, dayOffset);
        const dayFraction = exactDays - dayOffset;
        hoursFromStart = dayFraction * 24;
    }
    else if (config.mode === '6_months' || config.mode === '1_year') {
        const exactWeeks = dropX / config.subColWidth;
        const exactDays = exactWeeks * 7;
        const dayOffset = Math.floor(exactDays);
        newDateStr = addDays(startStr, dayOffset);
        const dayFraction = exactDays - dayOffset;
        hoursFromStart = dayFraction * 24;
    }

    const [year, month, day] = newDateStr.split('-');
    const baseHour = config.mode === 'hoy' ? 8 : 0;

    // Fase timezones: year/month/day/hora representan wall-clock-en-`userTz` (el usuario soltó
    // la tarea en esa posición viendo la grilla en su timezone configurado) -- `fromUserZoned`
    // calcula el instante UTC real que corresponde a esa hora en `userTz`, no en el navegador.
    const newStartLocal = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), baseHour, 0, 0, 0);
    newStartLocal.setMinutes(newStartLocal.getMinutes() + (hoursFromStart * 60));
    const newStart = fromUserZoned(newStartLocal, userTz);
    const newEnd = new Date(newStart.getTime() + (durationHours * 3600000));

    return {
        date: newDateStr,
        start_time: newStart.toISOString(),
        end_time: newEnd.toISOString()
    };
};

export const calculateResizeToTaskUpdate = (newWidth, config) => {
    let durationHours = 1;
    if (config.mode === 'hoy') {
        durationHours = newWidth / (config.subColWidth * 2);
    }
    else if (config.mode === 'week') {
        durationHours = newWidth / (config.subColWidth * (5 / 24));
    }
    else if (config.mode === 'month' || config.mode === '3_months') {
        durationHours = newWidth / (config.subColWidth / 24);
    }
    else if (config.mode === '6_months' || config.mode === '1_year') {
        durationHours = newWidth / (config.subColWidth / (24 * 7));
    }
    return Math.max(0.166667, durationHours); // minimum 10 minutes
};

// Robust Date Formatter Helper (e.g. 1 Jun 2026) -- función a nivel de módulo (fuera del
// componente, sin acceso a hooks), usa el singleton `i18n` para leer el idioma actual y
// traducir "Sin fecha"/los meses en vez del `t()` de un hook.
export const formatTaskDate = (dateStr) => {
    if (!dateStr) return i18n.t('common.noDate');
    try {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            const year = parts[0];
            const monthIndex = parseInt(parts[1], 10) - 1;
            const day = parseInt(parts[2], 10);
            const monthDate = new Date(Date.UTC(2000, monthIndex, 1));
            const monthText = new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'es-ES', { month: 'short', timeZone: 'UTC' }).format(monthDate);
            return `${day} ${monthText} ${year}`;
        }
    } catch (e) {
        // Fallback
    }
    return dateStr;
};

export const getTagColors = (tagName) => {
    const text = String(tagName || '');
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
        hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    return {
        color: `hsl(${h}, 85%, 70%)`,
        backgroundColor: `hsla(${h}, 85%, 70%, 0.3)`
    };
};

// Fase timezones: "hoy" se calcula en `userTz`, no en el navegador -- `target` se clona de
// `today` (ya "zoneado") en vez de partir de un `new Date()` nuevo, para que ambos queden en la
// misma representación y la resta de milisegundos (diffDays) sea correcta.
export const getRelativeDateDisplay = (dateStr, userTz) => {
    if (!dateStr) return { text: i18n.t('common.noDate'), colorClass: 'date-neutral' };
    const today = toUserZoned(new Date(), userTz);
    today.setHours(0,0,0,0);
    const target = new Date(today);
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        target.setFullYear(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    } else {
        return { text: dateStr, colorClass: 'date-neutral' };
    }
    target.setHours(0,0,0,0);

    if (isNaN(target.getTime())) return { text: dateStr, colorClass: 'date-neutral' };

    const diffTime = today.getTime() - target.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return { text: i18n.t('relativeDate.today'), colorClass: 'date-green' };
    if (diffDays === -1) return { text: i18n.t('relativeDate.tomorrow'), colorClass: 'date-neutral' };
    if (diffDays < 0) return { text: i18n.t('relativeDate.inDays', { count: Math.abs(diffDays) }), colorClass: 'date-neutral' };

    if (diffDays === 1) return { text: i18n.t('relativeDate.yesterday'), colorClass: 'date-green' };
    if (diffDays === 2) return { text: i18n.t('relativeDate.twoDaysAgo'), colorClass: 'date-green' };
    if (diffDays >= 3 && diffDays <= 6) return { text: i18n.t('relativeDate.daysAgo', { count: diffDays }), colorClass: 'date-green' };
    if (diffDays >= 7 && diffDays < 14) return { text: i18n.t('relativeDate.oneWeekAgo'), colorClass: 'date-red' };
    if (diffDays >= 14 && diffDays < 21) return { text: i18n.t('relativeDate.twoWeeksAgo'), colorClass: 'date-red' };
    if (diffDays >= 21 && diffDays < 28) return { text: i18n.t('relativeDate.threeWeeksAgo'), colorClass: 'date-red' };
    if (diffDays >= 28 && diffDays < 30) return { text: i18n.t('relativeDate.fourWeeksAgo'), colorClass: 'date-red' };
    if (diffDays >= 30 && diffDays < 60) return { text: i18n.t('relativeDate.oneMonthAgo'), colorClass: 'date-red' };
    if (diffDays >= 60) return { text: i18n.t('relativeDate.monthsAgo'), colorClass: 'date-red' };

    return { text: dateStr, colorClass: 'date-neutral' };
};

export const formatTiempoDisplay = (tiempoStr) => {
    if (!tiempoStr) return '5 min';
    return tiempoStr
        .replace(/minutos?/g, 'min')
        .replace(/horas?/g, 'hr')
        .replace(/días?/g, 'dia');
};

// Asigna cada tarea a un "track" (fila) del Gantt evitando solapes visuales (bloque + texto que
// se desborda). Pura — recibe el config del Gantt / el rango / el timezone.
export const assignGanttTracks = (tasksList, ganttConfig, startStr, userTz) => {
    const tasksWithPos = tasksList.map(task => {
        const pos = calculateTaskGanttPos(task, ganttConfig, startStr, userTz);

        const titleLength = task.title ? task.title.length : 0;
        const hasIcon = task.is_calendar_event || task.stage === 'recordatorio';
        // Avatar (24px) + Icon (~15px) + Text (~8px per char) + Padding (~12px) + extra buffer for bleeding text (20px)
        const estimatedTextWidth = 24 + (hasIcon ? 15 : 0) + (titleLength * 8) + 32;

        const visualStart = pos.startPos;
        const blockEnd = pos.startPos + pos.widthSize;
        const textEnd = pos.startPos + estimatedTextWidth;
        const visualEnd = Math.max(blockEnd, textEnd);

        return { task, visualStart, visualEnd };
    });

    tasksWithPos.sort((a, b) => a.visualStart - b.visualStart);

    const tracks = []; // Array de arrays con las tareas de cada track
    const taskTrackMap = {};

    tasksWithPos.forEach(item => {
        let assignedTrack = 0;
        while (true) {
            const hasOverlap = (tracks[assignedTrack] || []).some(t =>
                // solape en coordenadas de pixel absolutas, con un gap de 2px de seguridad
                item.visualStart < t.visualEnd + 2 && t.visualStart < item.visualEnd + 2
            );
            if (!hasOverlap) break;
            assignedTrack++;
        }
        if (!tracks[assignedTrack]) tracks[assignedTrack] = [];
        tracks[assignedTrack].push(item);
        taskTrackMap[item.task.id] = assignedTrack;
    });

    return { taskTrackMap, totalTracks: tracks.length };
};
